import React from 'react';
import { observer } from 'mobx-react';
import { Button } from 'components';
import { AssemblyExplorer, Brep, Part } from 'icons';

import { useClientStore } from 'apps/hooks';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { UiModes } from 'libs/rendering/gltf/config';
import { renderCustomIcon } from 'utils/jsxUtils';
import { themes } from 'styles/themes/wikifactoryNew';
import { CADGap } from 'styles/constants';
import { CADButtonWrapper } from 'apps/components/Frame/FrameSidebar/CADSidebar/CADButtonWrapper';

const baseButtonStyle = {
  padding: CADGap,
};

const getClassName = (isActive: boolean) => {
  if (isActive) {
    return 'border-wfBlue hover:bg-gray-200';
  }

  return 'hover:bg-gray-200';
};

const AssemblyExplorerButton = () => {
  const clientStore = useClientStore();
  const viewerControl = useViewerControl();
  if (!viewerControl.viewer) return null;

  const viewer = viewerControl?.viewer;
  const { settings, isDiffing } = viewer;
  const camera = settings.CurrentCamera();
  const loadingStatus = camera.GetLoadStatus();

  if (!camera || loadingStatus !== 0) return null;
  if (isDiffing) return null;

  const { debugFeatures } = viewerControl;
  const hasIntermediatorRole = clientStore.hasRole('INTERMEDIATOR');

  // only for single-part tabs
  const instance = camera.GetInstance();
  const body = instance?.GetBody();
  const brep = body?.GetBrep();

  const handleAssemblyTreeBtn = () => {
    viewerControl.UpdateUiMode(UiModes.assembly);
  };

  const handleGeometryExplorerBtn = () => {
    viewerControl.UpdateUiMode(UiModes.geometry);
  };

  const handleFeatureExplorerBtn = () => {
    if (viewerControl.uiMode === UiModes.collapsed) {
      viewerControl.UpdateUiMode(UiModes.feature);
    } else {
      camera.useSurfaceColors = false;
      viewerControl.UpdateUiMode(UiModes.collapsed);
    }
  };

  const isIntermediatorDebug = hasIntermediatorRole && debugFeatures;

  // assembly
  if (!camera.IsInstance()) {
    const isAssemblyTreeActive = viewerControl.uiMode === UiModes.assembly;

    return (
      <CADButtonWrapper
        isActive={isAssemblyTreeActive}
        className={getClassName(isAssemblyTreeActive)}
      >
        <Button
          data-testid='CADPage-AssemblyTreeAssemblyButton'
          size='medium'
          variant='transparent'
          style={baseButtonStyle}
          renderIcon={(isHover: boolean) =>
            renderCustomIcon(AssemblyExplorer, {
              isHover,
              isActive: isAssemblyTreeActive,
              activeFillColor: themes.color.wfBlue,
            })
          }
          onClick={handleAssemblyTreeBtn}
        />
      </CADButtonWrapper>
    );
  }

  // single part
  if (isDiffing) return null;

  if (isIntermediatorDebug) {
    // DEBUG: Feature browser
    const isActive = [UiModes.feature, UiModes.assembly].includes(
      viewerControl.uiMode,
    );

    return (
      <CADButtonWrapper isActive={isActive} className={getClassName(isActive)}>
        <Button
          data-testid='CADPage-AssemblyTreeDebugButton'
          size='medium'
          variant='transparent'
          style={baseButtonStyle}
          renderIcon={(isHover: boolean) =>
            renderCustomIcon(Part, {
              isHover,
              activeFillColor: themes.color.wfBlue,
            })
          }
          onClick={handleFeatureExplorerBtn}
        />
      </CADButtonWrapper>
    );
  }

  const shouldShowGeometry = false;
  if (brep && shouldShowGeometry) {
    // BREP geometry browser
    const isGeometryActive = viewerControl.uiMode === UiModes.geometry;

    return (
      <CADButtonWrapper
        isActive={isGeometryActive}
        className={getClassName(isGeometryActive)}
      >
        <Button
          data-testid='CADPage-AssemblyTreeBrepButton'
          size='medium'
          variant='transparent'
          style={baseButtonStyle}
          renderIcon={(isHover: boolean) =>
            renderCustomIcon(Brep, {
              isHover,
              isActive: isGeometryActive,
              activeFillColor: themes.color.wfBlue,
            })
          }
          onClick={handleGeometryExplorerBtn}
        />
      </CADButtonWrapper>
    );
  }

  return null;
};

export default observer(AssemblyExplorerButton);
